// The till mesh as its order store says it (UC-58, #775), for Sharing with
// this shop's tills (T28) and the link test (LinkTest.svelte).
//
// The store answers GET /store/mesh with the mesh's state and the shop's
// other tills, and POST /store/mesh/link-test with one other till's answer
// (internal/orderstore, native level 11). The screen keeps nothing of it: it
// reads the mesh as the sharing step shows. A read keeps the last good mesh
// and drops an answer older than one already taken; a link test keeps no
// state here.
import { store } from '../api/client';
import type { components } from '../api/store';

export type Mesh = components['schemas']['StoreMeshBody'];
export type MeshTill = components['schemas']['StoreMeshTill'];
/** The mesh's states this screen knows. */
export type MeshState = 'on' | 'needs_firewall' | 'off';

/** How long a read or a link test waits before the screen says it is waiting. */
export const MESH_SLOW_MS = 300;

/** How often the sharing step reads the mesh while it shows. */
export const MESH_READ_AGAIN_MS = 5000;

/** The state as the screen reads it: null before any answer, and a value it does not know as off. */
export function meshState(state: string | null | undefined): MeshState | null {
	if (state == null) return null;
	return state === 'on' || state === 'needs_firewall' ? state : 'off';
}

/** The store's mesh as the screen last read it. */
export class TillMesh {
	/** The store's last good answer; null until one came. */
	mesh = $state<Mesh | null>(null);
	/** A read is on its way, and past MESH_SLOW_MS once `slow`. */
	loading = $state(false);
	slow = $state(false);
	/** The last read got no answer or a refusal. */
	failed = $state(false);
	/** The failure was no answer or a 5xx, the only ones Retry is for (.claude/rules/web.md). */
	retryable = $state(false);
	/** A read finished, answered or not, so the sharing step can say what it holds. */
	settled = $state(false);
	#started = 0;
	#taken = 0;
	#reading = 0;
	/** A Turn on sharing shell call is on its way. */
	turning = $state(false);
	/** Windows allowed sharing, and the store has not said it is on yet. */
	allowed = $state(false);
	/** Windows declined, or its sharing command failed. */
	refusal = $state<'declined' | 'failed' | null>(null);
	/** The deadline for Windows' allowed answer to take effect. */
	#allowedTimer: ReturnType<typeof setTimeout> | undefined;

	get state(): MeshState | null {
		return meshState(this.mesh?.state);
	}

	/** Read the mesh from the store again; it never throws. */
	async read(): Promise<void> {
		const mine = ++this.#started;
		this.#reading = mine;
		this.loading = true;
		this.slow = false;
		const timer = setTimeout(() => {
			if (this.#reading === mine) this.slow = true;
		}, MESH_SLOW_MS);
		try {
			const { data, error, response } = await store.GET('/store/mesh');
			if (mine < this.#taken) return;
			this.#taken = mine;
			this.failed = !(error === undefined && data && response.status === 200);
			this.retryable = this.failed && response.status >= 500;
			if (!this.failed && data) {
				this.mesh = data;
				if (this.state === 'on') this.#clearAllowed();
			}
		} catch {
			if (mine >= this.#taken) {
				this.#taken = mine;
				this.failed = true;
				this.retryable = true;
			}
		} finally {
			clearTimeout(timer);
			this.settled = true;
			if (this.#reading === mine) {
				this.loading = false;
				this.slow = false;
			}
		}
	}

	/** Read at once and keep reading while the sharing step shows; stop on cleanup. */
	watch(): () => void {
		void this.read();
		const timer = setInterval(() => {
			if (!this.loading) void this.read();
		}, MESH_READ_AGAIN_MS);
		return () => clearInterval(timer);
	}

	/** Run Turn on sharing and retain Windows' answer while the Sheet is closed. */
	async turnOn(
		run: () => Promise<'allowed' | 'declined' | 'failed' | null>,
		wait: number,
	): Promise<void> {
		if (this.turning) return;
		this.turning = true;
		this.refusal = null;
		let outcome: 'allowed' | 'declined' | 'failed' | null;
		try {
			outcome = await run();
		} catch {
			outcome = 'failed';
		}
		this.turning = false;
		if (outcome === 'allowed') {
			this.allowed = true;
			this.#clearAllowedTimer();
			this.#allowedTimer = setTimeout(() => {
				this.#allowedTimer = undefined;
				this.allowed = false;
				if (this.state === 'needs_firewall') this.refusal = 'failed';
			}, wait);
		} else if (outcome !== null) {
			this.refusal = outcome;
		}
	}

	/** End Windows' allowed wait after the store reports sharing on. */
	#clearAllowed(): void {
		this.allowed = false;
		this.#clearAllowedTimer();
	}

	/** Cancel the current Windows allowed deadline, if one is set. */
	#clearAllowedTimer(): void {
		if (this.#allowedTimer !== undefined) clearTimeout(this.#allowedTimer);
		this.#allowedTimer = undefined;
	}
}

/** What a link test's answer means for the screen. */
export type LinkAnswer = 'answered' | 'not_heard' | 'no_answer' | 'mesh_off' | 'gone' | 'failed';

/**
 * A link test's answer: 200 answered; 409 MESH_UNREACHABLE with its reason,
 * not_heard or no_answer; 409 MESH_OFF; 404 NOT_FOUND, a till no longer of
 * the shop's keyring; anything else, a 422 or no answer included, failed.
 */
export function linkAnswer(
	status: number | null,
	error?: { code?: string; details?: Record<string, unknown> } | null,
): LinkAnswer {
	if (status === 200) return 'answered';
	if (status === 409 && error?.code === 'MESH_UNREACHABLE') {
		const reason = error.details?.reason;
		if (reason === 'not_heard' || reason === 'no_answer') return reason;
	}
	if (status === 409 && error?.code === 'MESH_OFF') return 'mesh_off';
	if (status === 404 && error?.code === 'NOT_FOUND') return 'gone';
	return 'failed';
}

/** Ask the store to test the link to one other till of the shop. */
export async function testLink(tillId: string): Promise<LinkAnswer> {
	try {
		const { error, response } = await store.POST('/store/mesh/link-test', { body: { tillId } });
		return linkAnswer(response.status, error);
	} catch {
		return 'failed';
	}
}

/** What a till's line says: when it was heard, not yet, or that it runs an older app. */
export function tillLine(till: MeshTill): 'heard' | 'not_heard' | 'legacy' {
	return till.speaks === 'legacy' ? 'legacy' : till.heardAt ? 'heard' : 'not_heard';
}

/** A till on an older Bosla app speaks the old format, so it offers no test. */
export function tillTestable(till: MeshTill): boolean {
	return till.speaks !== 'legacy';
}
